import {
  Asset,
  RegionalTab,
  AIAccordionTopic,
  MarketMoverItem,
  NewsItem,
  HistoricalPoint,
  ChartPeriod,
} from "../types/finance";
import { getAssetSector, getAssetTags } from "../utils/assetTaxonomy";
import { EXTRA_RAW_ASSETS } from "./extraMarketAssets";

// Helper to generate realistic historical curves
function generateTimeSeries(
  basePrice: number,
  volatility: number,
  trendPct: number,
  pointsCount: number,
  timeframe: ChartPeriod,
  benchmarkBase: number = 100
): HistoricalPoint[] {
  const points: HistoricalPoint[] = [];
  let currentPrice = basePrice * (1 - trendPct / 100);
  let currentBenchmark = benchmarkBase * (1 - trendPct / 150);

  const now = new Date();

  for (let i = 0; i < pointsCount; i++) {
    const progress = i / (pointsCount - 1);
    const noise = (Math.random() - 0.48) * (basePrice * volatility);
    currentPrice += (basePrice * (trendPct / 100)) / pointsCount + noise;
    currentBenchmark += (benchmarkBase * (trendPct / 150)) / pointsCount + (Math.random() - 0.48) * (benchmarkBase * 0.015);

    // ensure positive
    currentPrice = Math.max(currentPrice, basePrice * 0.4);

    let timeLabel = "";
    let dateLabel = "";

    if (timeframe === "1D") {
      const minutesFromOpen = Math.floor(progress * 420); // 7 hours (10:00 to 17:00)
      const h = 10 + Math.floor(minutesFromOpen / 60);
      const m = minutesFromOpen % 60;
      timeLabel = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      dateLabel = "Hoje";
    } else if (timeframe === "5D") {
      const dayOffset = 5 - Math.floor(progress * 5);
      const d = new Date(now.getTime() - dayOffset * 24 * 60 * 60 * 1000);
      timeLabel = `${d.getDate()}/${d.getMonth() + 1}`;
      dateLabel = `${d.getDate()}/${d.getMonth() + 1}`;
    } else if (timeframe === "1M" || timeframe === "6M" || timeframe === "YTD") {
      const days = timeframe === "1M" ? 30 : timeframe === "6M" ? 180 : 240;
      const dayOffset = days - Math.floor(progress * days);
      const d = new Date(now.getTime() - dayOffset * 24 * 60 * 60 * 1000);
      timeLabel = `${d.getDate()}/${d.getMonth() + 1}`;
      dateLabel = `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
    } else {
      const years = timeframe === "1A" ? 1 : timeframe === "5A" ? 5 : 10;
      const dayOffset = years * 365 - Math.floor(progress * years * 365);
      const d = new Date(now.getTime() - dayOffset * 24 * 60 * 60 * 1000);
      timeLabel = `${d.getMonth() + 1}/${String(d.getFullYear()).slice(2)}`;
      dateLabel = `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
    }

    const candleVariation = currentPrice * (volatility * 0.3);
    const open = currentPrice - (Math.random() - 0.5) * candleVariation;
    const close = currentPrice;
    const high = Math.max(open, close) + Math.random() * candleVariation;
    const low = Math.min(open, close) - Math.random() * candleVariation;

    points.push({
      time: timeLabel,
      date: dateLabel,
      price: Number(currentPrice.toFixed(2)),
      open: Number(open.toFixed(2)),
      high: Number(high.toFixed(2)),
      low: Number(low.toFixed(2)),
      close: Number(close.toFixed(2)),
      volume: Math.floor(100000 + Math.random() * 5000000),
      benchmarkPrice: Number(currentBenchmark.toFixed(2)),
    });
  }

  // Force last point to match exact final target
  if (points.length > 0) {
    points[points.length - 1].price = basePrice;
    points[points.length - 1].close = basePrice;
  }

  return points;
}

function buildAssetHistory(basePrice: number, changePercent: number): Record<ChartPeriod, HistoricalPoint[]> {
  return {
    "1D": generateTimeSeries(basePrice, 0.003, changePercent, 36, "1D", 100),
    "5D": generateTimeSeries(basePrice, 0.008, changePercent * 1.8, 30, "5D", 100),
    "1M": generateTimeSeries(basePrice, 0.015, changePercent * 3.2, 30, "1M", 100),
    "6M": generateTimeSeries(basePrice, 0.025, changePercent * 6.5, 30, "6M", 100),
    YTD: generateTimeSeries(basePrice, 0.028, changePercent * 7.1, 32, "YTD", 100),
    "1A": generateTimeSeries(basePrice, 0.035, changePercent * 12.0, 35, "1A", 100),
    "5A": generateTimeSeries(basePrice, 0.05, changePercent * 35.0, 40, "5A", 100),
    MÁX: generateTimeSeries(basePrice, 0.065, changePercent * 80.0, 45, "MÁX", 100),
  };
}

// Sparklines (7 points for miniature view)
function getSparkline(basePrice: number, isPositive: boolean): number[] {
  const points = [];
  let val = isPositive ? basePrice * 0.98 : basePrice * 1.02;
  for (let i = 0; i < 7; i++) {
    val += (Math.random() - (isPositive ? 0.4 : 0.6)) * (basePrice * 0.006);
    points.push(Number(val.toFixed(2)));
  }
  points[6] = basePrice;
  return points;
}

const RAW_ASSETS: Asset[] = [
  // --- EUA ---
  {
    id: "dow-jones",
    ticker: ".DJI",
    name: "Dow Jones Industrial Average",
    category: "EUA",
    currency: "USD",
    price: 43825.12,
    change: 285.4,
    changePercent: 0.66,
    exchange: "DJI / NYSE",
    sparkline: getSparkline(43825.12, true),
    historical: buildAssetHistory(43825.12, 0.66),
    annotations: {
      "1D": [
        { time: "10:30", label: "Abertura Wall Street", type: "open", price: 43550.0 },
        { time: "14:00", label: "Discurso Federal Reserve", type: "event", price: 43710.5 },
        { time: "16:00", label: "Fechamento Regular", type: "close", price: 43825.12 },
      ],
    },
    metrics: {
      open: 43560.2,
      high: 43910.45,
      low: 43520.1,
      high52w: 44485.6,
      low52w: 37122.9,
      prevClose: 43539.72,
      volume: "348,2 Mi",
      peRatio: "22,4x",
      dividendYield: "1,85%",
      marketCap: "US$ 13,8 Tri",
      beta: "0,88",
    },
    profile: {
      description:
        "O Dow Jones Industrial Average (DJIA) é um dos mais antigos e conceituados índices do mercado financeiro global, composto por 30 das maiores e mais influentes corporações de capital aberto dos Estados Unidos.",
      sector: "Índice de Mercado",
      headquarters: "Nova York, EUA",
      founded: "1896",
      websiteUrl: "https://www.spglobal.com/spdji/en/indices/equity/dow-jones-industrial-average/",
      websiteLabel: "S&P Dow Jones Indices",
      exchange: "NYSE / S&P DJI",
      currency: "USD",
    },
    relatedAssetIds: ["sp-500", "nasdaq", "russell-2000", "vix"],
  },
  {
    id: "sp-500",
    ticker: ".INX",
    name: "S&P 500",
    category: "EUA",
    currency: "USD",
    price: 5984.75,
    change: 42.15,
    changePercent: 0.71,
    exchange: "S&P Indices",
    sparkline: getSparkline(5984.75, true),
    historical: buildAssetHistory(5984.75, 0.71),
    annotations: {
      "1D": [
        { time: "10:30", label: "Abertura do Pregão", type: "open", price: 5945.0 },
        { time: "13:30", label: "Divulgação ISM Serviços", type: "event", price: 5968.2 },
      ],
    },
    metrics: {
      open: 5946.3,
      high: 5998.1,
      low: 5940.0,
      high52w: 6025.5,
      low52w: 4953.5,
      prevClose: 5942.6,
      volume: "2,41 Bi",
      peRatio: "24,8x",
      dividendYield: "1,42%",
      marketCap: "US$ 47,2 Tri",
      beta: "1,00",
    },
    profile: {
      description:
        "O Standard & Poor's 500 reúne as 500 principais empresas listadas nas bolsas americanas (NYSE e Nasdaq), cobrindo aproximadamente 80% do valor total de mercado de ações dos EUA.",
      sector: "Índice de Ações Amplas",
      headquarters: "Nova York, EUA",
      founded: "1957",
      websiteUrl: "https://www.spglobal.com/spdji/en/indices/equity/sp-500/",
      websiteLabel: "S&P Global",
      exchange: "CBOE / NYSE / NASDAQ",
      currency: "USD",
    },
    relatedAssetIds: ["dow-jones", "nasdaq", "vix", "russell-2000"],
  },
  {
    id: "nasdaq",
    ticker: ".IXIC",
    name: "Nasdaq Composite",
    category: "EUA",
    currency: "USD",
    price: 19280.4,
    change: 174.9,
    changePercent: 0.92,
    exchange: "NASDAQ",
    sparkline: getSparkline(19280.4, true),
    historical: buildAssetHistory(19280.4, 0.92),
    metrics: {
      open: 19120.0,
      high: 19340.2,
      low: 19095.4,
      high52w: 19485.0,
      low52w: 15300.2,
      prevClose: 19105.5,
      volume: "5,12 Bi",
      peRatio: "31,2x",
      dividendYield: "0,88%",
      marketCap: "US$ 26,5 Tri",
      beta: "1,24",
    },
    profile: {
      description:
        "O Nasdaq Composite reflete a cotação de mais de 3.000 ações negociadas na bolsa Nasdaq, com forte concentração em gigantes da tecnologia, semicondutores, inteligência artificial e biotecnologia.",
      sector: "Índice Tecnológico",
      headquarters: "Nova York, EUA",
      founded: "1971",
      websiteUrl: "https://www.nasdaq.com/market-activity/indexes/ixic",
      websiteLabel: "Nasdaq Official Portal",
      exchange: "NASDAQ",
      currency: "USD",
    },
    relatedAssetIds: ["sp-500", "dow-jones", "vix", "russell-2000"],
  },
  {
    id: "russell-2000",
    ticker: "RUT",
    name: "Russell 2000",
    category: "EUA",
    currency: "USD",
    price: 2315.6,
    change: -8.4,
    changePercent: -0.36,
    exchange: "FTSE Russell",
    sparkline: getSparkline(2315.6, false),
    historical: buildAssetHistory(2315.6, -0.36),
    metrics: {
      open: 2326.0,
      high: 2338.4,
      low: 2309.1,
      high52w: 2450.0,
      low52w: 1930.5,
      prevClose: 2324.0,
      volume: "1,28 Bi",
      peRatio: "19,5x",
      dividendYield: "1,55%",
      marketCap: "US$ 3,1 Tri",
      beta: "1,18",
    },
    profile: {
      description:
        "O Russell 2000 é o principal termômetro das pequenas e médias empresas (Small Caps) dos Estados Unidos, altamente sensível à saúde da economia doméstica e às taxas de juros americanas.",
      sector: "Índice Small Caps",
      headquarters: "Londres / Nova York",
      websiteUrl: "https://www.ftserussell.com/products/indices/russell-us",
      websiteLabel: "FTSE Russell",
      exchange: "CBOE",
      currency: "USD",
    },
    relatedAssetIds: ["sp-500", "dow-jones", "vix"],
  },
  {
    id: "vix",
    ticker: "VIX",
    name: "CBOE Volatility Index (Índice do Medo)",
    category: "EUA",
    currency: "USD",
    price: 14.82,
    change: -0.65,
    changePercent: -4.2,
    exchange: "CBOE",
    sparkline: getSparkline(14.82, false),
    historical: buildAssetHistory(14.82, -4.2),
    metrics: {
      open: 15.45,
      high: 15.8,
      low: 14.6,
      high52w: 65.73,
      low52w: 11.86,
      prevClose: 15.47,
      volume: "820 Mil",
      marketCap: "--",
      beta: "-0,75",
    },
    profile: {
      description:
        "Criado pela CBOE, o VIX mede a expectativa do mercado quanto à volatilidade implícita nas opções de curto prazo do S&P 500, funcionando como o principal indicador de apetite ao risco e aversão global.",
      sector: "Índice de Volatilidade",
      headquarters: "Chicago, EUA",
      websiteUrl: "https://www.cboe.com/tradable_products/vix/",
      websiteLabel: "CBOE Options Exchange",
      exchange: "CBOE",
      currency: "USD",
    },
    relatedAssetIds: ["sp-500", "dow-jones", "nasdaq"],
  },

  // --- Europa ---
  {
    id: "dax",
    ticker: "^GDAXI",
    name: "DAX 40",
    category: "Europa",
    currency: "EUR",
    price: 22890.3,
    change: 142.5,
    changePercent: 0.63,
    exchange: "Deutsche Börse",
    sparkline: getSparkline(22890.3, true),
    historical: buildAssetHistory(22890.3, 0.63),
    metrics: {
      open: 22770.0,
      high: 22930.5,
      low: 22750.0,
      high52w: 23150.0,
      low52w: 17620.0,
      prevClose: 22747.8,
      volume: "68,4 Mi",
      peRatio: "14,8x",
      dividendYield: "3,10%",
      marketCap: "€ 1,95 Tri",
      beta: "0,95",
    },
    profile: {
      description:
        "O DAX é o índice acionário de referência da Alemanha, acompanhando o desempenho das 40 maiores empresas com maior volume de negócios na Bolsa de Frankfurt (Deutsche Börse).",
      sector: "Índice Alemão",
      headquarters: "Frankfurt, Alemanha",
      founded: "1988",
      websiteUrl: "https://www.boerse-frankfurt.de/indices/dax",
      websiteLabel: "Börse Frankfurt",
      exchange: "XETRA",
      currency: "EUR",
    },
    relatedAssetIds: ["ftse-100", "cac-40", "euro-stoxx-50", "ibex-35"],
  },
  {
    id: "ftse-100",
    ticker: "^FTSE",
    name: "FTSE 100",
    category: "Europa",
    currency: "GBP",
    price: 8645.2,
    change: 35.8,
    changePercent: 0.42,
    exchange: "LSE",
    sparkline: getSparkline(8645.2, true),
    historical: buildAssetHistory(8645.2, 0.42),
    metrics: {
      open: 8612.0,
      high: 8660.1,
      low: 8598.0,
      high52w: 8820.0,
      low52w: 7850.0,
      prevClose: 8609.4,
      volume: "740 Mi",
      peRatio: "12,9x",
      dividendYield: "3,85%",
      marketCap: "£ 2,15 Tri",
      beta: "0,82",
    },
    profile: {
      description:
        "O Financial Times Stock Exchange 100 Index representa as 100 maiores empresas cotadas na London Stock Exchange (LSE), com forte peso de commodities, energia e setor bancário internacional.",
      sector: "Índice Britânico",
      headquarters: "Londres, Reino Unido",
      websiteUrl: "https://www.londonstockexchange.com/indices/ftse-100",
      websiteLabel: "London Stock Exchange",
      exchange: "LSE",
      currency: "GBP",
    },
    relatedAssetIds: ["dax", "cac-40", "euro-stoxx-50"],
  },
  {
    id: "cac-40",
    ticker: "^FCHI",
    name: "CAC 40",
    category: "Europa",
    currency: "EUR",
    price: 8120.7,
    change: -22.3,
    changePercent: -0.27,
    exchange: "Euronext Paris",
    sparkline: getSparkline(8120.7, false),
    historical: buildAssetHistory(8120.7, -0.27),
    metrics: {
      open: 8145.0,
      high: 8160.0,
      low: 8095.0,
      high52w: 8259.0,
      low52w: 7100.0,
      prevClose: 8143.0,
      volume: "58,2 Mi",
      peRatio: "15,2x",
      dividendYield: "2,95%",
      marketCap: "€ 2,42 Tri",
      beta: "0,98",
    },
    profile: {
      description:
        "Índice de referência da Bolsa de Paris (Euronext Paris), com forte presença de conglomerados de luxo (LVMH, Hermès, Kering), energia, cosméticos e aeroespacial.",
      sector: "Índice Francês",
      headquarters: "Paris, França",
      websiteUrl: "https://live.euronext.com/en/product/indices/FR0003500008-XPAR",
      websiteLabel: "Euronext",
      exchange: "Euronext Paris",
      currency: "EUR",
    },
    relatedAssetIds: ["dax", "euro-stoxx-50", "ibex-35"],
  },
  {
    id: "ibex-35",
    ticker: "^IBEX",
    name: "IBEX 35",
    category: "Europa",
    currency: "EUR",
    price: 12480.9,
    change: 98.4,
    changePercent: 0.79,
    exchange: "Bolsas y Mercados Españoles",
    sparkline: getSparkline(12480.9, true),
    historical: buildAssetHistory(12480.9, 0.79),
    metrics: {
      open: 12390.0,
      high: 12510.0,
      low: 12380.0,
      high52w: 12600.0,
      low52w: 9850.0,
      prevClose: 12382.5,
      volume: "42,1 Mi",
      peRatio: "11,8x",
      dividendYield: "4,15%",
      marketCap: "€ 680 Bi",
      beta: "0,92",
    },
    profile: {
      description:
        "O índice oficial do mercado de ações espanhol, composto pelas 35 empresas com maior liquidez negociadas no Sistema de Interconexão Bursátil Espanhol (SIBE).",
      sector: "Índice Espanhol",
      headquarters: "Madri, Espanha",
      websiteUrl: "https://www.bolsamadrid.es",
      websiteLabel: "Bolsa de Madrid",
      exchange: "BME",
      currency: "EUR",
    },
    relatedAssetIds: ["dax", "cac-40", "euro-stoxx-50"],
  },
  {
    id: "euro-stoxx-50",
    ticker: "^STOXX50E",
    name: "Euro Stoxx 50",
    category: "Europa",
    currency: "EUR",
    price: 5190.4,
    change: 31.2,
    changePercent: 0.6,
    exchange: "STOXX",
    sparkline: getSparkline(5190.4, true),
    historical: buildAssetHistory(5190.4, 0.6),
    metrics: {
      open: 5165.0,
      high: 5205.0,
      low: 5158.0,
      high52w: 5280.0,
      low52w: 4400.0,
      prevClose: 5159.2,
      volume: "128,5 Mi",
      peRatio: "14,5x",
      dividendYield: "3,25%",
      marketCap: "€ 4,1 Tri",
      beta: "0,96",
    },
    profile: {
      description:
        "O principal índice de blue-chips da Zona do Euro, composto pelas 50 maiores empresas de 8 países da Europa Continental (Alemanha, França, Holanda, Espanha, Itália, etc.).",
      sector: "Índice Pan-Europeu",
      headquarters: "Zurique / Frankfurt",
      websiteUrl: "https://www.stoxx.com",
      websiteLabel: "Qontigo STOXX",
      exchange: "Euronext / Deutsche Börse",
      currency: "EUR",
    },
    relatedAssetIds: ["dax", "ftse-100", "cac-40"],
  },

  // --- Ásia ---
  {
    id: "nikkei-225",
    ticker: "^N225",
    name: "Nikkei 225",
    category: "Ásia",
    currency: "JPY",
    price: 39420.0,
    change: 480.0,
    changePercent: 1.23,
    exchange: "Tokyo Stock Exchange",
    sparkline: getSparkline(39420.0, true),
    historical: buildAssetHistory(39420.0, 1.23),
    annotations: {
      "1D": [
        { time: "09:00", label: "Abertura Tóquio", type: "open", price: 39050.0 },
        { time: "11:30", label: "Pausa Almoço TSE", type: "event", price: 39220.0 },
        { time: "15:00", label: "Fechamento Tóquio", type: "close", price: 39420.0 },
      ],
    },
    metrics: {
      open: 39010.0,
      high: 39550.0,
      low: 38980.0,
      high52w: 42426.0,
      low52w: 31100.0,
      prevClose: 38940.0,
      volume: "1,85 Bi",
      peRatio: "17,2x",
      dividendYield: "1,75%",
      marketCap: "¥ 980 Tri",
      beta: "0,90",
    },
    profile: {
      description:
        "O Nikkei-225 Stock Average é o principal índice de ações do Japão, calculado desde 1950 pelo jornal Nihon Keizai Shimbun, incluindo líderes como Toyota, Sony, Nintendo e Tokyo Electron.",
      sector: "Índice Japonês",
      headquarters: "Tóquio, Japão",
      founded: "1950",
      websiteUrl: "https://indexes.nikkei.co.jp/en/nkave/",
      websiteLabel: "Nikkei Indexes",
      exchange: "TSE",
      currency: "JPY",
    },
    relatedAssetIds: ["hang-seng", "sse-composite", "nifty-50", "kospi"],
  },
  {
    id: "sse-composite",
    ticker: "000001.SS",
    name: "SSE Composite (Xangai)",
    category: "Ásia",
    currency: "CNY",
    price: 3385.4,
    change: 22.8,
    changePercent: 0.68,
    exchange: "Shanghai Stock Exchange",
    sparkline: getSparkline(3385.4, true),
    historical: buildAssetHistory(3385.4, 0.68),
    metrics: {
      open: 3365.0,
      high: 3398.0,
      low: 3358.0,
      high52w: 3674.0,
      low52w: 2689.0,
      prevClose: 3362.6,
      volume: "485 Mi",
      peRatio: "13,6x",
      dividendYield: "2,65%",
      marketCap: "¥ 52 Tri",
      beta: "0,74",
    },
    profile: {
      description:
        "O Shanghai Stock Exchange Composite reúne todas as ações (A-shares e B-shares) cotadas na Bolsa de Valores de Xangai, refletindo diretamente o ritmo e estímulos da economia da China.",
      sector: "Índice Chinês",
      headquarters: "Xangai, China",
      websiteUrl: "http://www.sse.com.cn",
      websiteLabel: "Shanghai Stock Exchange",
      exchange: "SSE",
      currency: "CNY",
    },
    relatedAssetIds: ["nikkei-225", "hang-seng", "nifty-50"],
  },
  {
    id: "hang-seng",
    ticker: "^HSI",
    name: "Hang Seng Index",
    category: "Ásia",
    currency: "HKD",
    price: 20680.5,
    change: -115.2,
    changePercent: -0.55,
    exchange: "Hong Kong Exchanges",
    sparkline: getSparkline(20680.5, false),
    historical: buildAssetHistory(20680.5, -0.55),
    metrics: {
      open: 20820.0,
      high: 20890.0,
      low: 20590.0,
      high52w: 23241.0,
      low52w: 14794.0,
      prevClose: 20795.7,
      volume: "1,92 Bi",
      peRatio: "9,8x",
      dividendYield: "3,90%",
      marketCap: "HK$ 38 Tri",
      beta: "1,05",
    },
    profile: {
      description:
        "O índice Hang Seng monitora as maiores corporações cotadas na Bolsa de Hong Kong, com enorme relevância para gigantes de internet (Tencent, Alibaba, Meituan) e financeiras asiáticas.",
      sector: "Índice Hong Kong",
      headquarters: "Hong Kong",
      websiteUrl: "https://www.hsi.com.hk",
      websiteLabel: "Hang Seng Indexes",
      exchange: "HKEX",
      currency: "HKD",
    },
    relatedAssetIds: ["sse-composite", "nikkei-225", "kospi"],
  },
  {
    id: "bse-sensex",
    ticker: "^BSESN",
    name: "BSE SENSEX",
    category: "Ásia",
    currency: "INR",
    price: 81450.0,
    change: 560.0,
    changePercent: 0.69,
    exchange: "Bombay Stock Exchange",
    sparkline: getSparkline(81450.0, true),
    historical: buildAssetHistory(81450.0, 0.69),
    metrics: {
      open: 80980.0,
      high: 81620.0,
      low: 80890.0,
      high52w: 85978.0,
      low52w: 70319.0,
      prevClose: 80890.0,
      volume: "38,2 Mi",
      peRatio: "23,1x",
      dividendYield: "1,15%",
      marketCap: "₹ 410 Tri",
      beta: "0,85",
    },
    profile: {
      description:
        "O SENSEX 30 é o índice de ações mais antigo da Índia, composto por 30 das empresas mais representativas e financeiramente sólidas da Bolsa de Valores de Bombaim.",
      sector: "Índice Indiano",
      headquarters: "Mumbai, Índia",
      websiteUrl: "https://www.bseindia.com",
      websiteLabel: "BSE India",
      exchange: "BSE",
      currency: "INR",
    },
    relatedAssetIds: ["nifty-50", "nikkei-225"],
  },
  {
    id: "nifty-50",
    ticker: "^NSEI",
    name: "NIFTY 50",
    category: "Ásia",
    currency: "INR",
    price: 24780.2,
    change: 155.4,
    changePercent: 0.63,
    exchange: "NSE India",
    sparkline: getSparkline(24780.2, true),
    historical: buildAssetHistory(24780.2, 0.63),
    metrics: {
      open: 24650.0,
      high: 24840.0,
      low: 24620.0,
      high52w: 26277.0,
      low52w: 21285.0,
      prevClose: 24624.8,
      volume: "310 Mi",
      peRatio: "22,8x",
      dividendYield: "1,20%",
      marketCap: "₹ 430 Tri",
      beta: "0,86",
    },
    profile: {
      description:
        "O NIFTY 50 é o principal índice da National Stock Exchange of India (NSE), cobrindo 13 setores da pujante economia indiana e servindo de referência para derivativos internacionais.",
      sector: "Índice Indiano",
      headquarters: "Mumbai, Índia",
      websiteUrl: "https://www.nseindia.com",
      websiteLabel: "NSE India",
      exchange: "NSE",
      currency: "INR",
    },
    relatedAssetIds: ["bse-sensex", "nikkei-225"],
  },
  {
    id: "kospi",
    ticker: "^KS11",
    name: "KOSPI (Coreia do Sul)",
    category: "Ásia",
    currency: "KRW",
    price: 2685.2,
    change: 24.6,
    changePercent: 0.92,
    exchange: "Korea Exchange",
    sparkline: getSparkline(2685.2, true),
    historical: buildAssetHistory(2685.2, 0.92),
    metrics: {
      open: 2665.0,
      high: 2695.0,
      low: 2660.0,
      high52w: 2896.0,
      low52w: 2400.0,
      prevClose: 2660.6,
      volume: "480 Mi",
      peRatio: "15,8x",
      dividendYield: "2,10%",
      marketCap: "₩ 2.150 Tri",
      beta: "1,08",
    },
    profile: {
      description:
        "O Korea Composite Stock Price Index (KOSPI) é o índice das ações ordinárias negociadas na Korea Exchange, capitaneado por líderes mundiais de semicondutores como Samsung Electronics e SK Hynix.",
      sector: "Índice Sul-Coreano",
      headquarters: "Seul, Coreia do Sul",
      websiteUrl: "http://global.krx.co.kr",
      websiteLabel: "KRX Korea Exchange",
      exchange: "KRX",
      currency: "KRW",
    },
    relatedAssetIds: ["nikkei-225", "hang-seng"],
  },

  // --- América Latina ---
  {
    id: "ibovespa",
    ticker: "IBOV",
    name: "Índice Bovespa (Ibovespa)",
    category: "América Latina",
    currency: "BRL",
    price: 167921.8,
    change: -1270.2,
    changePercent: -0.75,
    exchange: "B3 - Brasil, Bolsa, Balcão",
    sparkline: getSparkline(167921.8, false),
    historical: buildAssetHistory(167921.8, -0.75),
    annotations: {
      "1D": [
        { time: "10:00", label: "Abertura B3", type: "open", price: 135800.0 },
        { time: "14:30", label: "Fluxo Estrangeiro Positivo", type: "event", price: 136150.0 },
        { time: "17:00", label: "Fechamento B3", type: "close", price: 136420.5 },
      ],
    },
    metrics: {
      open: 135800.0,
      high: 136890.0,
      low: 135620.0,
      high52w: 139500.0,
      low52w: 118600.0,
      prevClose: 135800.0,
      volume: "R$ 28,4 Bi",
      peRatio: "9,8x",
      dividendYield: "6,95%",
      marketCap: "R$ 4,95 Tri",
      beta: "1,00",
    },
    profile: {
      description:
        "O Ibovespa é o principal indicador do desempenho das ações negociadas na B3 e reúne as empresas mais representativas do mercado de capitais brasileiro, como Petrobras, Vale, Itaú e Ambev.",
      sector: "Índice de Ações Brasil",
      headquarters: "São Paulo, Brasil",
      founded: "1968",
      websiteUrl: "https://www.b3.com.br/pt_br/market-data-e-indices/indices/indices-amplos/ibovespa.htm",
      websiteLabel: "Portal B3 Oficial",
      exchange: "B3",
      currency: "BRL",
    },
    relatedAssetIds: ["petr4", "vale3", "itub4", "sp-latin-america", "ibrx-brasil"],
  },
  {
    id: "sp-latin-america",
    ticker: ".SPLATA40",
    name: "S&P Latin America 40",
    category: "América Latina",
    currency: "USD",
    price: 2680.4,
    change: 32.1,
    changePercent: 1.21,
    exchange: "S&P DJI",
    sparkline: getSparkline(2680.4, true),
    historical: buildAssetHistory(2680.4, 1.21),
    metrics: {
      open: 2650.0,
      high: 2695.0,
      low: 2642.0,
      high52w: 2980.0,
      low52w: 2310.0,
      prevClose: 2648.3,
      volume: "US$ 1,2 Bi",
      peRatio: "10,2x",
      dividendYield: "5,40%",
      marketCap: "US$ 640 Bi",
      beta: "1,12",
    },
    profile: {
      description:
        "O S&P Latin America 40 reúne as 40 maiores e mais líquidas empresas da América Latina (Brasil, México, Chile, Colômbia e Peru), negociadas nas bolsas locais e via ADRs em Nova York.",
      sector: "Índice Regional LatAm",
      headquarters: "Nova York / São Paulo",
      websiteUrl: "https://www.spglobal.com/spdji/en/indices/equity/sp-latin-america-40/",
      websiteLabel: "S&P Global LatAm",
      exchange: "S&P DJI",
      currency: "USD",
    },
    relatedAssetIds: ["ibovespa", "petr4", "vale3"],
  },
  {
    id: "igovernanca",
    ticker: "IGOV",
    name: "Índice de Governança Corporativa (IGC)",
    category: "América Latina",
    currency: "BRL",
    price: 4985.0,
    change: 38.5,
    changePercent: 0.78,
    exchange: "B3",
    sparkline: getSparkline(4985.0, true),
    historical: buildAssetHistory(4985.0, 0.78),
    metrics: {
      open: 4950.0,
      high: 4998.0,
      low: 4945.0,
      high52w: 5210.0,
      low52w: 4320.0,
      prevClose: 4946.5,
      volume: "R$ 18,2 Bi",
      dividendYield: "5,90%",
      marketCap: "R$ 3,9 Tri",
      beta: "0,97",
    },
    profile: {
      description:
        "Mede o desempenho de empresas que adotam práticas diferenciadas de governança corporativa na B3 (Novo Mercado, Nível 2 e Nível 1), garantindo maior transparência e direitos aos acionistas minoritários.",
      sector: "Governança Corporativa",
      headquarters: "São Paulo, Brasil",
      websiteUrl: "https://www.b3.com.br",
      websiteLabel: "B3 Índices",
      exchange: "B3",
      currency: "BRL",
    },
    relatedAssetIds: ["ibovespa", "ibrx-brasil", "wege3"],
  },
  {
    id: "ibrx-brasil",
    ticker: "IBRA",
    name: "Índice Brasil Amplo (IBrA B3)",
    category: "América Latina",
    currency: "BRL",
    price: 5280.9,
    change: 49.3,
    changePercent: 0.94,
    exchange: "B3",
    sparkline: getSparkline(5280.9, true),
    historical: buildAssetHistory(5280.9, 0.94),
    metrics: {
      open: 5235.0,
      high: 5295.0,
      low: 5230.0,
      high52w: 5490.0,
      low52w: 4610.0,
      prevClose: 5231.6,
      volume: "R$ 28,4 Bi",
      peRatio: "9,8x",
      dividendYield: "6,20%",
      marketCap: "R$ 4,6 Tri",
      beta: "0,99",
    },
    profile: {
      description:
        "O IBrA tem como objetivo oferecer uma visão ampla do mercado acionário brasileiro, medindo o desempenho de todos os ativos negociados na B3 que atendem a critérios rigorosos de negociabilidade e presença em pregão.",
      sector: "Mercado Amplo",
      headquarters: "São Paulo, Brasil",
      websiteUrl: "https://www.b3.com.br",
      websiteLabel: "B3",
      exchange: "B3",
      currency: "BRL",
    },
    relatedAssetIds: ["ibovespa", "petr4", "vale3"],
  },
  {
    id: "petr4",
    ticker: "PETR4",
    name: "Petróleo Brasileiro S.A. - Petrobras PN",
    category: "América Latina",
    currency: "BRL",
    price: 38.74,
    change: 0.82,
    changePercent: 2.16,
    exchange: "B3",
    sparkline: getSparkline(38.74, true),
    historical: buildAssetHistory(38.74, 2.16),
    metrics: {
      open: 38.05,
      high: 38.95,
      low: 37.9,
      high52w: 42.8,
      low52w: 32.1,
      prevClose: 37.92,
      volume: "R$ 1,84 Bi",
      peRatio: "4,6x",
      dividendYield: "14,8%",
      marketCap: "R$ 510 Bi",
      beta: "1,22",
      avgVolume: "R$ 1,65 Bi",
    },
    profile: {
      description:
        "A Petrobras é uma das maiores empresas integradas de energia do mundo, com liderança global em exploração e produção de petróleo e gás em águas ultraprofundas (Pré-Sal brasileiro).",
      sector: "Petróleo, Gás e Biocombustíveis",
      industry: "Exploração e Refino",
      headquarters: "Rio de Janeiro, RJ, Brasil",
      ceo: "Magda Chambriard",
      employees: "46.000+",
      founded: "1953",
      websiteUrl: "https://ri.petrobras.com.br",
      websiteLabel: "Relações com Investidores Petrobras",
      exchange: "B3 / NYSE (ADR)",
      currency: "BRL",
    },
    relatedAssetIds: ["ibovespa", "vale3", "petroleo-brent", "itub4"],
  },
  {
    id: "vale3",
    ticker: "VALE3",
    name: "Vale S.A. - ON",
    category: "América Latina",
    currency: "BRL",
    price: 59.85,
    change: -0.75,
    changePercent: -1.24,
    exchange: "B3",
    sparkline: getSparkline(59.85, false),
    historical: buildAssetHistory(59.85, -1.24),
    metrics: {
      open: 60.4,
      high: 60.8,
      low: 59.5,
      high52w: 75.4,
      low52w: 54.2,
      prevClose: 60.6,
      volume: "R$ 1,42 Bi",
      peRatio: "6,8x",
      dividendYield: "9,2%",
      marketCap: "R$ 272 Bi",
      beta: "1,15",
    },
    profile: {
      description:
        "A Vale é uma das maiores mineradoras globais, líder mundial na produção de minério de ferro, pelotas e de níquel para transição energética, com atuação integrada em ferrovia e portos.",
      sector: "Materiais Básicos",
      industry: "Mineração",
      headquarters: "Rio de Janeiro, RJ, Brasil",
      ceo: "Gustavo Pimenta",
      employees: "70.000+",
      founded: "1942",
      websiteUrl: "https://www.vale.com/pt/investidores",
      websiteLabel: "Portal RI Vale",
      exchange: "B3 / NYSE (ADR)",
      currency: "BRL",
    },
    relatedAssetIds: ["ibovespa", "petr4", "sse-composite"],
  },
  {
    id: "itub4",
    ticker: "ITUB4",
    name: "Itaú Unibanco Holding S.A. - PN",
    category: "América Latina",
    currency: "BRL",
    price: 36.42,
    change: 0.38,
    changePercent: 1.05,
    exchange: "B3",
    sparkline: getSparkline(36.42, true),
    historical: buildAssetHistory(36.42, 1.05),
    metrics: {
      open: 36.1,
      high: 36.65,
      low: 35.95,
      high52w: 38.5,
      low52w: 30.2,
      prevClose: 36.04,
      volume: "R$ 980 Mi",
      peRatio: "8,9x",
      dividendYield: "7,4%",
      marketCap: "R$ 355 Bi",
      beta: "0,88",
    },
    profile: {
      description:
        "Maior banco privado da América Latina em valor de mercado e ativos, o Itaú Unibanco atua em serviços bancários comerciais, corporate investment banking, cartões e gestão de fortunas.",
      sector: "Financeiro",
      industry: "Bancos Múltiplos",
      headquarters: "São Paulo, SP, Brasil",
      ceo: "Milton Maluhy Filho",
      employees: "95.000+",
      founded: "1924",
      websiteUrl: "https://www.itau.com.br/relacoes-com-investidores",
      websiteLabel: "RI Itaú Unibanco",
      exchange: "B3 / NYSE",
      currency: "BRL",
    },
    relatedAssetIds: ["ibovespa", "bbas3", "petr4"],
  },
  {
    id: "wege3",
    ticker: "WEGE3",
    name: "WEG S.A. - ON",
    category: "América Latina",
    currency: "BRL",
    price: 54.12,
    change: 1.18,
    changePercent: 2.23,
    exchange: "B3",
    sparkline: getSparkline(54.12, true),
    historical: buildAssetHistory(54.12, 2.23),
    metrics: {
      open: 53.0,
      high: 54.5,
      low: 52.8,
      high52w: 58.2,
      low52w: 34.1,
      prevClose: 52.94,
      volume: "R$ 480 Mi",
      peRatio: "34,5x",
      dividendYield: "1,85%",
      marketCap: "R$ 227 Bi",
      beta: "0,78",
    },
    profile: {
      description:
        "Multinacional brasileira de bens de capital, a WEG é referência mundial em motores elétricos, geradores, transformadores, tintas industriais e soluções para energia renovável e automação 4.0.",
      sector: "Bens Industriais",
      industry: "Máquinas e Equipamentos",
      headquarters: "Jaraguá do Sul, SC, Brasil",
      ceo: "Alberto Yoshikazu Kuba",
      employees: "40.000+",
      founded: "1961",
      websiteUrl: "https://ri.weg.net",
      websiteLabel: "Relações com Investidores WEG",
      exchange: "B3",
      currency: "BRL",
    },
    relatedAssetIds: ["ibovespa", "igovernanca"],
  },
  {
    id: "bbdc4",
    ticker: "BBDC4",
    name: "Banco Bradesco S.A. - PN",
    category: "América Latina",
    currency: "BRL",
    price: 14.85,
    change: 0.18,
    changePercent: 1.23,
    exchange: "B3",
    sparkline: getSparkline(14.85, true),
    historical: buildAssetHistory(14.85, 1.23),
    metrics: {
      open: 14.67,
      high: 14.96,
      low: 14.60,
      high52w: 17.20,
      low52w: 12.10,
      prevClose: 14.67,
      volume: "R$ 490 Mi",
      peRatio: "8,4x",
      dividendYield: "6,8%",
      marketCap: "R$ 158 Bi",
      beta: "0,92",
    },
    profile: {
      description:
        "O Banco Bradesco é um dos maiores conglomerados financeiros da América Latina, atuando fortemente em serviços bancários, crédito comercial e na liderança do mercado segurador por meio do Grupo Bradesco Seguros.",
      sector: "Financeiro",
      industry: "Bancos Múltiplos",
      headquarters: "Osasco, SP, Brasil",
      ceo: "Marcelo de Araújo Noronha",
      employees: "86.000+",
      founded: "1943",
      websiteUrl: "https://banco.bradesco/ri",
      websiteLabel: "RI Bradesco",
      exchange: "B3 / NYSE",
      currency: "BRL",
    },
    relatedAssetIds: ["itub4", "bbas3", "ibovespa"],
  },

  // --- Moedas ---
  {
    id: "usd-brl",
    ticker: "USD/BRL",
    name: "Dólar Comercial Americano / Real Brasileiro",
    category: "Moedas",
    currency: "BRL",
    price: 5.724,
    change: -0.038,
    changePercent: -0.66,
    exchange: "FX Interbancário",
    sparkline: getSparkline(5.724, false),
    historical: buildAssetHistory(5.724, -0.66),
    metrics: {
      open: 5.762,
      high: 5.778,
      low: 5.715,
      high52w: 6.22,
      low52w: 4.85,
      prevClose: 5.762,
      volume: "US$ 14,5 Bi",
      marketCap: "--",
    },
    profile: {
      description:
        "A paridade USD/BRL é a taxa de câmbio mais líquida e importante para a economia brasileira, refletindo fluxos comerciais, diferenciais de juros (Selic vs Fed Funds) e risco-país.",
      sector: "Mercado de Câmbio (Forex)",
      headquarters: "Brasília / Nova York",
      websiteUrl: "https://www.bcb.gov.br",
      websiteLabel: "Banco Central do Brasil",
      exchange: "B3 / FX",
      currency: "BRL",
    },
    relatedAssetIds: ["eur-brl", "gbp-brl", "usd-eur", "ibovespa"],
  },
  {
    id: "eur-brl",
    ticker: "EUR/BRL",
    name: "Euro / Real Brasileiro",
    category: "Moedas",
    currency: "BRL",
    price: 6.012,
    change: -0.024,
    changePercent: -0.4,
    exchange: "FX Interbancário",
    sparkline: getSparkline(6.012, false),
    historical: buildAssetHistory(6.012, -0.4),
    metrics: {
      open: 6.042,
      high: 6.058,
      low: 6.002,
      high52w: 6.64,
      low52w: 5.28,
      prevClose: 6.036,
      volume: "€ 3,8 Bi",
      marketCap: "--",
    },
    profile: {
      description:
        "Cotação da moeda única da Zona do Euro frente ao Real brasileiro, de extrema relevância para transações comerciais com o bloco europeu e investimentos internacionais.",
      sector: "Mercado de Câmbio",
      headquarters: "Frankfurt, Alemanha",
      websiteUrl: "https://www.ecb.europa.eu",
      websiteLabel: "Banco Central Europeu (BCE)",
      exchange: "FX",
      currency: "BRL",
    },
    relatedAssetIds: ["usd-brl", "usd-eur", "gbp-brl"],
  },
  {
    id: "gbp-brl",
    ticker: "GBP/BRL",
    name: "Libra Esterlina / Real Brasileiro",
    category: "Moedas",
    currency: "BRL",
    price: 7.235,
    change: 0.018,
    changePercent: 0.25,
    exchange: "FX",
    sparkline: getSparkline(7.235, true),
    historical: buildAssetHistory(7.235, 0.25),
    metrics: {
      open: 7.215,
      high: 7.265,
      low: 7.202,
      high52w: 7.92,
      low52w: 6.18,
      prevClose: 7.217,
      volume: "£ 1,2 Bi",
      marketCap: "--",
    },
    profile: {
      description:
        "Taxa de câmbio entre a Libra Esterlina britânica e o Real, essencial para comércio bilateral, serviços financeiros de Londres e turismo.",
      sector: "Forex",
      headquarters: "Londres, Reino Unido",
      websiteUrl: "https://www.bankofengland.co.uk",
      websiteLabel: "Bank of England",
      exchange: "FX",
      currency: "BRL",
    },
    relatedAssetIds: ["usd-brl", "eur-brl", "ftse-100"],
  },
  {
    id: "jpy-brl",
    ticker: "JPY/BRL",
    name: "Iene Japonês / Real Brasileiro (100 JPY)",
    category: "Moedas",
    currency: "BRL",
    price: 3.82,
    change: 0.035,
    changePercent: 0.92,
    exchange: "FX",
    sparkline: getSparkline(3.82, true),
    historical: buildAssetHistory(3.82, 0.92),
    metrics: {
      open: 3.78,
      high: 3.84,
      low: 3.77,
      high52w: 4.25,
      low52w: 3.12,
      prevClose: 3.785,
      volume: "¥ 85 Bi",
    },
    profile: {
      description:
        "Cotação de 100 Ienes japoneses expressa em Reais, diretamente conectada às taxas de juros do Banco do Japão (BOJ) e ao fluxo de carry trade global.",
      sector: "Forex",
      headquarters: "Tóquio, Japão",
      websiteUrl: "https://www.boj.or.jp/en/",
      websiteLabel: "Bank of Japan",
      exchange: "FX",
      currency: "BRL",
    },
    relatedAssetIds: ["nikkei-225", "usd-brl"],
  },
  {
    id: "usd-eur",
    ticker: "USD/EUR",
    name: "Dólar Americano / Euro (EUR/USD Inverso)",
    category: "Moedas",
    currency: "EUR",
    price: 0.952,
    change: -0.003,
    changePercent: -0.31,
    exchange: "FX Interbancário",
    sparkline: getSparkline(0.952, false),
    historical: buildAssetHistory(0.952, -0.31),
    metrics: {
      open: 0.955,
      high: 0.958,
      low: 0.951,
      high52w: 0.985,
      low52w: 0.905,
      prevClose: 0.955,
      volume: "US$ 650 Bi",
    },
    profile: {
      description:
        "O par EUR/USD é o ativo financeiro mais negociado do planeta, movimentando trilhões de dólares diariamente no mercado interbancário mundial.",
      sector: "Forex Global",
      headquarters: "Nova York / Frankfurt",
      websiteUrl: "https://www.federalreserve.gov",
      websiteLabel: "Federal Reserve",
      exchange: "FX",
      currency: "EUR",
    },
    relatedAssetIds: ["usd-brl", "eur-brl", "sp-500", "dax"],
  },
  {
    id: "chf-brl",
    ticker: "CHF/BRL",
    name: "Franco Suíço / Real Brasileiro",
    category: "Moedas",
    currency: "BRL",
    price: 6.251,
    change: 0.016,
    changePercent: 0.26,
    exchange: "FX Interbancário",
    sparkline: getSparkline(6.251, true),
    historical: buildAssetHistory(6.251, 0.26),
    metrics: {
      open: 6.235,
      high: 6.284,
      low: 6.211,
      high52w: 6.72,
      low52w: 5.42,
      prevClose: 6.235,
      volume: "CHF 620 Mi",
      marketCap: "--",
    },
    profile: {
      description:
        "Cotação do Franco Suíço frente ao Real brasileiro. O CHF é historicamente a moeda de refúgio seguro mais sólida do planeta, respaldada pela estabilidade política e solidez financeira da Suíça.",
      sector: "Mercado de Câmbio (Forex)",
      headquarters: "Berna / Zurique, Suíça",
      websiteUrl: "https://www.snb.ch",
      websiteLabel: "Swiss National Bank (SNB)",
      exchange: "FX",
      currency: "BRL",
    },
    relatedAssetIds: ["usd-brl", "eur-brl"],
  },
  {
    id: "usd-jpy",
    ticker: "USD/JPY",
    name: "Dólar Americano / Iene Japonês",
    category: "Moedas",
    currency: "JPY",
    price: 152.4,
    change: 0.35,
    changePercent: 0.23,
    exchange: "FX Interbancário",
    sparkline: getSparkline(152.4, true),
    historical: buildAssetHistory(152.4, 0.23),
    metrics: {
      open: 152.05,
      high: 152.88,
      low: 151.8,
      high52w: 161.9,
      low52w: 140.2,
      prevClose: 152.05,
      volume: "US$ 180 Bi",
      marketCap: "--",
    },
    profile: {
      description:
        "O par USD/JPY é o segundo mais negociado do mundo no mercado Forex, sendo o principal termômetro das operações de carry trade global e da política monetária do Banco do Japão (BOJ).",
      sector: "Forex Global",
      headquarters: "Tóquio / Nova York",
      websiteUrl: "https://www.boj.or.jp",
      websiteLabel: "Bank of Japan",
      exchange: "FX",
      currency: "JPY",
    },
    relatedAssetIds: ["jpy-brl", "nikkei-225", "usd-brl"],
  },

  // --- Criptomoedas ---
  {
    id: "bitcoin",
    ticker: "BTC",
    name: "Bitcoin",
    category: "Criptomoedas",
    currency: "USD",
    price: 88430.0,
    change: 1250.0,
    changePercent: 1.43,
    exchange: "Mercado Cripto Global",
    sparkline: getSparkline(88430.0, true),
    historical: buildAssetHistory(88430.0, 1.43),
    annotations: {
      "1D": [
        { time: "03:00", label: "Volume Ásia / ETF Inflow", type: "event", price: 87200.0 },
        { time: "11:00", label: "Rompimento Resistência US$ 88k", type: "high", price: 88900.0 },
      ],
    },
    metrics: {
      open: 87180.0,
      high: 88950.0,
      low: 86900.0,
      high52w: 104000.0,
      low52w: 52000.0,
      prevClose: 87180.0,
      volume: "US$ 38 Bi (Global)",
      marketCap: "US$ 1,92 Tri",
      avgVolume: "US$ 38 Bi",
    },
    profile: {
      description:
        "O Bitcoin é a primeira e mais valiosa moeda digital descentralizada do mundo, baseada na tecnologia blockchain, com oferta fixa limitada em 21 milhões de unidades e reconhecida como ouro digital.",
      sector: "Criptoativos / Reserva de Valor Digital",
      headquarters: "Rede Descentralizada Global",
      founded: "2009 (Satoshi Nakamoto)",
      websiteUrl: "https://bitcoin.org",
      websiteLabel: "Bitcoin Open Source Foundation",
      exchange: "Exchanges Globais / B3 (ETFs)",
      currency: "USD",
    },
    relatedAssetIds: ["ether", "solana", "xrp", "ouro-futuro"],
  },
  {
    id: "ether",
    ticker: "ETH",
    name: "Ether (Ethereum)",
    category: "Criptomoedas",
    currency: "USD",
    price: 2654.0,
    change: 45.0,
    changePercent: 1.72,
    exchange: "Ethereum Network",
    sparkline: getSparkline(2654.0, true),
    historical: buildAssetHistory(2654.0, 1.72),
    metrics: {
      open: 2609.0,
      high: 2680.0,
      low: 2595.0,
      high52w: 4090.0,
      low52w: 2150.0,
      prevClose: 2609.0,
      volume: "US$ 14 Bi",
      marketCap: "US$ 320 Bi",
    },
    profile: {
      description:
        "Ether é a criptomoeda nativa da rede Ethereum, a plataforma líder mundial para contratos inteligentes (smart contracts), finanças descentralizadas (DeFi) e tokenização de ativos reais (RWA).",
      sector: "Smart Contracts / DeFi",
      headquarters: "Zug, Suíça (Ethereum Foundation)",
      founded: "2015 (Vitalik Buterin)",
      websiteUrl: "https://ethereum.org",
      websiteLabel: "Ethereum Foundation",
      exchange: "Binance / Coinbase / Mercado Bitcoin",
      currency: "USD",
    },
    relatedAssetIds: ["bitcoin", "solana", "xrp"],
  },
  {
    id: "solana",
    ticker: "SOL",
    name: "Solana",
    category: "Criptomoedas",
    currency: "USD",
    price: 142.18,
    change: 5.2,
    changePercent: 3.8,
    exchange: "Solana Network",
    sparkline: getSparkline(142.18, true),
    historical: buildAssetHistory(142.18, 3.8),
    metrics: {
      open: 136.98,
      high: 144.5,
      low: 135.2,
      high52w: 260.0,
      low52w: 85.0,
      prevClose: 136.98,
      volume: "US$ 4.2 Bi",
      marketCap: "US$ 68 Bi",
    },
    profile: {
      description:
        "A Solana é uma blockchain de camada 1 de altíssimo desempenho, capaz de processar milhares de transações por segundo com taxas mínimas usando os mecanismos Proof of History e Proof of Stake.",
      sector: "Infraestrutura Blockchain Layer 1",
      headquarters: "São Francisco, EUA",
      websiteUrl: "https://solana.com",
      websiteLabel: "Solana Foundation",
      exchange: "Exchanges",
      currency: "USD",
    },
    relatedAssetIds: ["bitcoin", "ether", "xrp"],
  },
  {
    id: "xrp",
    ticker: "XRP",
    name: "XRP (Ripple)",
    category: "Criptomoedas",
    currency: "USD",
    price: 1.65,
    change: 0.04,
    changePercent: 2.48,
    exchange: "XRP Ledger",
    sparkline: getSparkline(1.65, true),
    historical: buildAssetHistory(1.65, 2.48),
    metrics: {
      open: 1.61,
      high: 1.69,
      low: 1.58,
      high52w: 2.8,
      low52w: 0.45,
      prevClose: 1.61,
      volume: "US$ 2.8 Bi",
      marketCap: "US$ 95 Bi",
    },
    profile: {
      description:
        "O XRP é o ativo digital nativo do XRP Ledger, projetado para facilitar pagamentos e liquidações transfronteiriças instantâneas e de baixo custo entre instituições bancárias globais.",
      sector: "Pagamentos e Liquidação Interbancária",
      headquarters: "São Francisco, EUA",
      websiteUrl: "https://ripple.com/xrp/",
      websiteLabel: "Ripple Official",
      exchange: "Global Exchanges",
      currency: "USD",
    },
    relatedAssetIds: ["bitcoin", "ether", "solana", "dogecoin"],
  },
  {
    id: "dogecoin",
    ticker: "DOGE",
    name: "Dogecoin",
    category: "Criptomoedas",
    currency: "USD",
    price: 0.185,
    change: -0.004,
    changePercent: -2.12,
    exchange: "Dogecoin Blockchain",
    sparkline: getSparkline(0.185, false),
    historical: buildAssetHistory(0.185, -2.12),
    metrics: {
      open: 0.189,
      high: 0.194,
      low: 0.181,
      high52w: 0.48,
      low52w: 0.07,
      prevClose: 0.189,
      volume: "US$ 1.1 Bi",
      marketCap: "US$ 27 Bi",
    },
    profile: {
      description:
        "Criada em 2013 como uma paródia bem-humorada do Bitcoin, a Dogecoin se tornou uma das maiores criptomoedas do mundo por capitalização, apoiada por uma imensa comunidade e ampla aceitação para micropagamentos.",
      sector: "Memecoin / Pagamentos P2P",
      headquarters: "Comunidade Open Source",
      websiteUrl: "https://dogecoin.com",
      websiteLabel: "Dogecoin Portal",
      exchange: "Exchanges",
      currency: "USD",
    },
    relatedAssetIds: ["bitcoin", "solana", "xrp"],
  },
  {
    id: "bnb",
    ticker: "BNB",
    name: "BNB (Binance Coin)",
    category: "Criptomoedas",
    currency: "USD",
    price: 648.5,
    change: 12.3,
    changePercent: 1.93,
    exchange: "BNB Chain / Binance",
    sparkline: getSparkline(648.5, true),
    historical: buildAssetHistory(648.5, 1.93),
    metrics: {
      open: 636.2,
      high: 654.8,
      low: 632.1,
      high52w: 720.6,
      low52w: 220.0,
      prevClose: 636.2,
      volume: "US$ 1.8 Bi",
      marketCap: "US$ 94 Bi",
    },
    profile: {
      description:
        "O BNB alimenta o ecossistema BNB Chain e a maior corretora de criptoativos do mundo, a Binance. Utilizado para taxas de gás com desconto, governança descentralizada e ecossistema DeFi multichain.",
      sector: "Exchange Token / Smart Contracts",
      headquarters: "Global Descentralizado",
      websiteUrl: "https://www.bnbchain.org",
      websiteLabel: "BNB Chain Official",
      exchange: "Binance",
      currency: "USD",
    },
    relatedAssetIds: ["bitcoin", "ether", "solana"],
  },

  // --- Contratos futuros ---
  {
    id: "petroleo-brent",
    ticker: "BZ=F",
    name: "Petróleo Brent Futuro (Contrato Contínuo)",
    category: "Contratos futuros",
    currency: "USD",
    price: 76.45,
    change: 1.35,
    changePercent: 1.8,
    exchange: "ICE Futures Europe",
    sparkline: getSparkline(76.45, true),
    historical: buildAssetHistory(76.45, 1.8),
    metrics: {
      open: 75.1,
      high: 76.9,
      low: 74.85,
      high52w: 92.4,
      low52w: 68.2,
      prevClose: 75.1,
      volume: "340 Mil contratos",
      marketCap: "--",
    },
    profile: {
      description:
        "O Petróleo Brent é o principal benchmark internacional para a precificação de barris de petróleo bruto negociados no mercado global, produzido no Mar do Norte e negociado na ICE em Londres.",
      sector: "Commodities Energéticas",
      headquarters: "Londres, Reino Unido",
      websiteUrl: "https://www.theice.com/products/219/Brent-Crude-Futures",
      websiteLabel: "Intercontinental Exchange (ICE)",
      exchange: "ICE",
      currency: "USD",
    },
    relatedAssetIds: ["petr4", "gas-natural", "ouro-futuro", "sp-500-futuros"],
  },
  {
    id: "ouro-futuro",
    ticker: "GC=F",
    name: "Ouro Futuro (Oz Troy 100)",
    category: "Contratos futuros",
    currency: "USD",
    price: 2948.5,
    change: 18.2,
    changePercent: 0.62,
    exchange: "COMEX / NYMEX",
    sparkline: getSparkline(2948.5, true),
    historical: buildAssetHistory(2948.5, 0.62),
    metrics: {
      open: 2930.3,
      high: 2955.0,
      low: 2925.0,
      high52w: 2985.0,
      low52w: 2010.0,
      prevClose: 2930.3,
      volume: "185 Mil contratos",
    },
    profile: {
      description:
        "O contrato futuro de Ouro na COMEX é o principal instrumento de hedge e investimento em metal precioso do mundo, funcionando historicamente como proteção contra inflação e tensões geopolíticas.",
      sector: "Metais Preciosos / Hedge",
      headquarters: "Chicago / Nova York",
      websiteUrl: "https://www.cmegroup.com/markets/metals/precious/gold.html",
      websiteLabel: "CME Group",
      exchange: "COMEX",
      currency: "USD",
    },
    relatedAssetIds: ["bitcoin", "petroleo-brent", "sp-500-futuros"],
  },
  {
    id: "soja-futuro",
    ticker: "ZS=F",
    name: "Soja em Grão Futuro (Chicago)",
    category: "Contratos futuros",
    currency: "USD",
    price: 1045.2,
    change: -8.6,
    changePercent: -0.82,
    exchange: "CBOT (Chicago Board of Trade)",
    sparkline: getSparkline(1045.2, false),
    historical: buildAssetHistory(1045.2, -0.82),
    metrics: {
      open: 1053.8,
      high: 1059.0,
      low: 1041.5,
      high52w: 1260.0,
      low52w: 960.0,
      prevClose: 1053.8,
      volume: "142 Mil contratos",
    },
    profile: {
      description:
        "O contrato futuro de soja na CBOT é a principal referência global de preços agrícolas, de extrema relevância para a balança comercial e o agronegócio exportador do Brasil.",
      sector: "Commodities Agrícolas",
      headquarters: "Chicago, EUA",
      websiteUrl: "https://www.cmegroup.com",
      websiteLabel: "CME Group CBOT",
      exchange: "CBOT",
      currency: "USD",
    },
    relatedAssetIds: ["petroleo-brent", "ibovespa", "usd-brl"],
  },
  {
    id: "gas-natural",
    ticker: "NG=F",
    name: "Gás Natural Henry Hub Futuro",
    category: "Contratos futuros",
    currency: "USD",
    price: 3.42,
    change: 0.11,
    changePercent: 3.32,
    exchange: "NYMEX",
    sparkline: getSparkline(3.42, true),
    historical: buildAssetHistory(3.42, 3.32),
    metrics: {
      open: 3.31,
      high: 3.48,
      low: 3.28,
      high52w: 4.15,
      low52w: 1.62,
      prevClose: 3.31,
      volume: "98 Mil contratos",
    },
    profile: {
      description:
        "O Henry Hub Natural Gas é o ponto de entrega físico e referência internacional para contratos de gás natural nos Estados Unidos e para o mercado de GNL exportado.",
      sector: "Commodities Energéticas",
      headquarters: "Luisiana / Nova York",
      websiteUrl: "https://www.cmegroup.com",
      websiteLabel: "NYMEX",
      exchange: "NYMEX",
      currency: "USD",
    },
    relatedAssetIds: ["petroleo-brent", "ouro-futuro"],
  },
  {
    id: "sp-500-futuros",
    ticker: "ES=F",
    name: "E-mini S&P 500 Futuros",
    category: "Contratos futuros",
    currency: "USD",
    price: 5992.5,
    change: 41.5,
    changePercent: 0.7,
    exchange: "CME (Chicago Mercantile Exchange)",
    sparkline: getSparkline(5992.5, true),
    historical: buildAssetHistory(5992.5, 0.7),
    metrics: {
      open: 5951.0,
      high: 6005.0,
      low: 5948.0,
      high52w: 6040.0,
      low52w: 4970.0,
      prevClose: 5951.0,
      volume: "1,45 Mi contratos",
    },
    profile: {
      description:
        "O contrato E-mini S&P 500 é o derivativo de índice acionário mais líquido do mundo, negociado quase 24 horas por dia e servindo como termômetro de abertura para Wall Street e bolsas globais.",
      sector: "Derivativos de Índices",
      headquarters: "Chicago, EUA",
      websiteUrl: "https://www.cmegroup.com/markets/equities/sp/e-mini-sandp500.html",
      websiteLabel: "CME Group Equities",
      exchange: "CME",
      currency: "USD",
    },
    relatedAssetIds: ["sp-500", "dow-jones", "nasdaq", "vix"],
  },
  {
    id: "prata",
    ticker: "SI=F",
    name: "Prata Futura (Silver COMEX)",
    category: "Contratos futuros",
    currency: "USD",
    price: 31.85,
    change: 0.42,
    changePercent: 1.34,
    exchange: "COMEX / NYMEX",
    sparkline: getSparkline(31.85, true),
    historical: buildAssetHistory(31.85, 1.34),
    metrics: {
      open: 31.43,
      high: 32.10,
      low: 31.25,
      high52w: 35.40,
      low52w: 22.10,
      prevClose: 31.43,
      volume: "68 Mil contratos",
      marketCap: "--",
    },
    profile: {
      description:
        "O contrato futuro de Prata negociado na COMEX/NYMEX é uma referência global para aplicações industriais (eletrônica, painéis solares fotovoltaicos e baterias) e reserva de valor como metal precioso.",
      sector: "Metais Preciosos & Industriais",
      headquarters: "Nova York, EUA",
      websiteUrl: "https://www.cmegroup.com/markets/metals/precious/silver.html",
      websiteLabel: "CME Group Silver",
      exchange: "COMEX",
      currency: "USD",
    },
    relatedAssetIds: ["ouro-futuro", "petroleo-brent", "bitcoin"],
  },
  {
    id: "petroleo-wti",
    ticker: "CL=F",
    name: "Petróleo WTI Futuro (Crude Oil)",
    category: "Contratos futuros",
    currency: "USD",
    price: 70.60,
    change: 0.95,
    changePercent: 1.36,
    exchange: "NYMEX",
    sparkline: getSparkline(70.60, true),
    historical: buildAssetHistory(70.60, 1.36),
    metrics: {
      open: 69.65,
      high: 71.20,
      low: 69.40,
      high52w: 87.60,
      low52w: 65.20,
      prevClose: 69.65,
      volume: "295 Mil contratos",
      marketCap: "--",
    },
    profile: {
      description:
        "O West Texas Intermediate (WTI) é o petróleo de referência dos Estados Unidos, com entrega física em Cushing, Oklahoma. É um dos contratos de derivativos de maior liquidez e sensibilidade geopolítica do mundo.",
      sector: "Commodities Energéticas",
      headquarters: "Nova York / Cushing, EUA",
      websiteUrl: "https://www.cmegroup.com/markets/energy/crude-oil/light-sweet-crude.html",
      websiteLabel: "NYMEX Crude Oil",
      exchange: "NYMEX",
      currency: "USD",
    },
    relatedAssetIds: ["petroleo-brent", "gas-natural", "petr4"],
  },
];

const COMBINED_RAW_ASSETS: Asset[] = [...RAW_ASSETS, ...EXTRA_RAW_ASSETS];

export const ALL_ASSETS: Asset[] = COMBINED_RAW_ASSETS.map((asset) => ({
  ...asset,
  sector: asset.sector || getAssetSector(asset),
  tags: asset.tags || getAssetTags(asset),
}));

// Map of AI Accordion topics by Regional Tab
export const AI_MARKET_TOPICS_BY_REGION: Record<RegionalTab, AIAccordionTopic[]> = {
  EUA: [
    {
      id: "fed-rates",
      title: "Decisões de Juros do Federal Reserve & Curva de Títulos (Treasuries)",
      summary:
        "Investidores precificam cortes graduais nas taxas de juros americanas (Fed Funds) diante de dados de inflação PCE alinhados com a meta de 2%.",
      detailedContext:
        "O rendimento dos títulos do Tesouro de 10 anos recuou para 4,25%, estimulando o apetite por risco e ações de tecnologia com múltiplos alongados.",
      impactTag: "Impacto Alto • Macroeconomia",
      trend: "bullish",
      region: "EUA",
    },
    {
      id: "ai-tech-rally",
      title: "Big Techs & Boom de Infraestrutura em Inteligência Artificial",
      summary:
        "Gigantes do Vale do Silício elevam Capex em data centers e semicondutores para sustentar a demanda corporativa por modelos de linguagem e chips aceleradores.",
      detailedContext:
        "O setor de semicondutores responde por mais de 35% dos ganhos do S&P 500 no ano, com fortes margens operacionais e pedidos contratados até 2027.",
      impactTag: "Impacto Alto • Tecnologia & IA",
      trend: "bullish",
      region: "EUA",
    },
    {
      id: "us-earnings",
      title: "Temporada de Balanços Corporativos de Wall Street",
      summary:
        "Mais de 78% das empresas do S&P 500 superaram as estimativas de lucro por ação (EPS), impulsionadas por ganhos de produtividade e poder de repasse de preços.",
      detailedContext:
        "Destaque positivo para serviços financeiros, saúde e plataformas de computação em nuvem, compensando a moderação no varejo de consumo discricionário.",
      impactTag: "Impacto Médio • Balanços",
      trend: "neutral",
      region: "EUA",
    },
  ],
  Europa: [
    {
      id: "ecb-policy",
      title: "Banco Central Europeu (BCE) e Ritmo de Afrouxamento Monetário",
      summary:
        "Christine Lagarde reforça dependência de dados para novas reduções na taxa de depósito, com a indústria da Alemanha demonstrando sinais de estabilização.",
      detailedContext:
        "O DAX alemão opera próximo a máximas históricas impulsionado por exportadoras de tecnologia médica e engenharia de precisão.",
      impactTag: "Impacto Alto • Política Monetária",
      trend: "bullish",
      region: "Europa",
    },
    {
      id: "energy-defense",
      title: "Reconfiguração Energética e Gastos em Defesa na União Europeia",
      summary:
        "Orçamentos de segurança aumentados nos países-membros da OTAN impulsionam empresas de defesa aeroespacial e contratos de infraestrutura sustentável.",
      detailedContext:
        "Os estoques europeus de gás natural encontram-se confortáveis, reduzindo a volatilidade dos custos de produção industrial para o inverno.",
      impactTag: "Impacto Médio • Geopolítica & Energia",
      trend: "neutral",
      region: "Europa",
    },
  ],
  Ásia: [
    {
      id: "china-stimulus",
      title: "Pacote Fiscal de Estímulo e Mercado Imobiliário na China",
      summary:
        "Pequim acelera emissão de títulos soberanos especiais para financiar infraestrutura de alta tecnologia e estabilizar o mercado residencial urbano.",
      detailedContext:
        "Bolsas de Xangai e Hong Kong registram entrada de fluxo estrangeiro em busca de valuations descontados nas gigantes de e-commerce e veículos elétricos.",
      impactTag: "Impacto Alto • Estímulo Estatal",
      trend: "bullish",
      region: "Ásia",
    },
    {
      id: "boj-yen",
      title: "Normalização do Banco do Japão (BOJ) e Desmonte de Carry Trade",
      summary:
        "O Banco do Japão sinaliza aperto gradual nas taxas, monitorando o crescimento salarial e a competitividade do Iene no comércio exterior.",
      detailedContext:
        "A governança corporativa no Japão continua gerando recompras recordes de ações pelas empresas do Nikkei 225.",
      impactTag: "Impacto Alto • Câmbio & Carry Trade",
      trend: "neutral",
      region: "Ásia",
    },
  ],
  "América Latina": [
    {
      id: "copom-fiscal",
      title: "Copom (Banco Central do Brasil) e Cenário Fiscal Doméstico",
      summary:
        "Mercado calibra as expectativas para a taxa Selic frente às metas de primário e despesas públicas, mantendo o diferencial de juros favorável ao Real.",
      detailedContext:
        "O Ibovespa se beneficia de fluxo comprador de investidores não residentes em ações de dividendos (bancos e energia elétrica) com múltiplos atraentes.",
      impactTag: "Impacto Alto • Renda Variável Brasil",
      trend: "bullish",
      region: "América Latina",
    },
    {
      id: "commodities-latam",
      title: "Superciclo de Transição Energética e Exportação de Commodities",
      summary:
        "A demanda por cobre, minério de ferro de alta pureza e petróleo do Pré-Sal sustenta balanças comerciais robustas no Brasil, Chile e Peru.",
      detailedContext:
        "Petrobras e Vale continuam com forte geração de fluxo de caixa livre, distribuindo dividendos expressivos acima da média global.",
      impactTag: "Impacto Médio • Commodities",
      trend: "bullish",
      region: "América Latina",
    },
  ],
  Moedas: [
    {
      id: "dxy-flow",
      title: "Índice Dólar (DXY) e Fluxo Cambial para Mercados Emergentes",
      summary:
        "O arrefecimento do dólar global alivia a pressão sobre moedas de países emergentes, com o Real brasileiro operando em patamares mais firmes.",
      detailedContext:
        "Swaps cambiais e atuações de bancos centrais mantêm a volatilidade implícita do Forex sob controle nas principais praças de negociação.",
      impactTag: "Impacto Alto • Câmbio Global",
      trend: "neutral",
      region: "Moedas",
    },
  ],
  Criptomoedas: [
    {
      id: "crypto-institutional",
      title: "Aporte Institucional em ETFs Spot de Bitcoin e Ethereum",
      summary:
        "Entradas líquidas consistentes de fundos soberanos e gestoras institucionais consolidam o Bitcoin como reserva estratégica de capital.",
      detailedContext:
        "A contínua expansão de redes de camada 2 e a tokenização de ativos do mundo real (RWA) aceleram o volume transacionado em DeFi.",
      impactTag: "Impacto Alto • Adoção Institucional",
      trend: "bullish",
      region: "Criptomoedas",
    },
  ],
  "Contratos futuros": [
    {
      id: "brent-supply",
      title: "Gestão de Oferta da OPEP+ e Riscos de Rotas de Navegação",
      summary:
        "A extensão dos cortes voluntários de produção de petróleo pela OPEP+ e a recuperação da demanda industrial mantêm o Brent sustentado acima de US$ 75.",
      detailedContext:
        "Contratos agrícolas de soja e milho precificam condições climáticas favoráveis na safra da América do Sul.",
      impactTag: "Impacto Médio • Energia e Agro",
      trend: "bullish",
      region: "Contratos futuros",
    },
  ],
};

// 3-column Market Movers
export const TOP_MOVERS_DATA: {
  mostActive: MarketMoverItem[];
  topGainers: MarketMoverItem[];
  topLosers: MarketMoverItem[];
} = {
  mostActive: [
    { id: "petr4", ticker: "PETR4", name: "Petrobras PN", price: 38.74, change: 0.82, changePercent: 2.16, currency: "BRL", volume: "R$ 1,84 Bi" },
    { id: "vale3", ticker: "VALE3", name: "Vale ON", price: 59.85, change: -0.75, changePercent: -1.24, currency: "BRL", volume: "R$ 1,42 Bi" },
    { id: "itub4", ticker: "ITUB4", name: "Itaú Unibanco PN", price: 36.42, change: 0.38, changePercent: 1.05, currency: "BRL", volume: "R$ 980 Mi" },
    { id: "bbas3", ticker: "BBAS3", name: "Banco do Brasil ON", price: 27.95, change: 0.42, changePercent: 1.53, currency: "BRL", volume: "R$ 650 Mi" },
    { id: "b3sa3", ticker: "B3SA3", name: "B3 S.A. ON", price: 11.28, change: 0.22, changePercent: 1.99, currency: "BRL", volume: "R$ 510 Mi" },
    { id: "mglu3", ticker: "MGLU3", name: "Magazine Luiza ON", price: 9.45, change: -0.28, changePercent: -2.88, currency: "BRL", volume: "R$ 490 Mi" },
  ],
  topGainers: [
    { id: "prio3", ticker: "PRIO3", name: "PetroRio ON", price: 44.8, change: 2.35, changePercent: 5.54, currency: "BRL", volume: "R$ 380 Mi" },
    { id: "embra3", ticker: "EMBR3", name: "Embraer ON", price: 56.7, change: 2.7, changePercent: 5.0, currency: "BRL", volume: "R$ 410 Mi" },
    { id: "totv3", ticker: "TOTS3", name: "Totvs ON", price: 32.15, change: 1.25, changePercent: 4.05, currency: "BRL", volume: "R$ 290 Mi" },
    { id: "wege3", ticker: "WEGE3", name: "WEG ON", price: 54.12, change: 1.18, changePercent: 2.23, currency: "BRL", volume: "R$ 480 Mi" },
    { id: "sula11", ticker: "EQTL3", name: "Equatorial ON", price: 34.9, change: 0.72, changePercent: 2.11, currency: "BRL", volume: "R$ 220 Mi" },
    { id: "petr4", ticker: "PETR4", name: "Petrobras PN", price: 38.74, change: 0.82, changePercent: 2.16, currency: "BRL", volume: "R$ 1,84 Bi" },
  ],
  topLosers: [
    { id: "azulp4", ticker: "AZUL4", name: "Azul PN", price: 4.12, change: -0.32, changePercent: -7.21, currency: "BRL", volume: "R$ 210 Mi" },
    { id: "cmin3", ticker: "CMIN3", name: "CSN Mineração ON", price: 5.48, change: -0.24, changePercent: -4.2, currency: "BRL", volume: "R$ 195 Mi" },
    { id: "mglu3", ticker: "MGLU3", name: "Magazine Luiza ON", price: 9.45, change: -0.28, changePercent: -2.88, currency: "BRL", volume: "R$ 490 Mi" },
    { id: "csna3", ticker: "CSNA3", name: "Siderúrgica Nacional ON", price: 10.35, change: -0.29, changePercent: -2.73, currency: "BRL", volume: "R$ 310 Mi" },
    { id: "vale3", ticker: "VALE3", name: "Vale ON", price: 59.85, change: -0.75, changePercent: -1.24, currency: "BRL", volume: "R$ 1,42 Bi" },
    { id: "crfb3", ticker: "CRFB3", name: "Carrefour Brasil ON", price: 8.72, change: -0.1, changePercent: -1.13, currency: "BRL", volume: "R$ 140 Mi" },
  ],
};

// Portal News Feed
export const PORTAL_NEWS: NewsItem[] = [
  {
    id: "news-1",
    title: "Ibovespa supera 134 mil pontos com entrada de capital externo e disparada de petrolíferas",
    source: "Valor Econômico",
    timeAgo: "há 22 min",
    category: "Mercados B3",
    url: "https://valor.globo.com/financas/",
    relatedTickers: ["IBOV", "PETR4", "VALE3"],
    sentiment: "positive",
  },
  {
    id: "news-2",
    title: "S&P 500 e Nasdaq batem recordes com projeções robustas de data centers e inteligência artificial",
    source: "Bloomberg Brasil",
    timeAgo: "há 45 min",
    category: "Wall Street",
    url: "https://www.bloomberglinea.com.br/mercados/",
    relatedTickers: [".INX", ".IXIC"],
    sentiment: "positive",
  },
  {
    id: "news-3",
    title: "Dólar recua para R$ 5,72 com dados de inflação nos EUA e atratividade do carrego brasileiro",
    source: "Reuters Financial",
    timeAgo: "há 1 hora",
    category: "Câmbio & Forex",
    url: "https://www.reuters.com/markets/",
    relatedTickers: ["USD/BRL"],
    sentiment: "positive",
  },
  {
    id: "news-4",
    title: "Bitcoin consolida patamar acima de R$ 540.000 após recorde de aportes institucionais em ETFs",
    source: "InfoMoney Cripto",
    timeAgo: "há 2 horas",
    category: "Criptoativos",
    url: "https://www.infomoney.com.br/cripto/",
    relatedTickers: ["BTC/BRL", "ETH/BRL"],
    sentiment: "positive",
  },
  {
    id: "news-5",
    title: "BCE sinaliza cautela sobre ritmo de cortes de juros diante de custos salariais na Europa",
    source: "Financial Times",
    timeAgo: "há 3 horas",
    category: "Economia Global",
    url: "https://www.ft.com/markets",
    relatedTickers: ["^GDAXI", "^STOXX50E"],
    sentiment: "neutral",
  },
  {
    id: "news-6",
    title: "Petróleo fecha em alta com extensão de cortes voluntários pela OPEP+ e tensões no Oriente Médio",
    source: "Investing.com",
    timeAgo: "há 4 horas",
    category: "Commodities",
    url: "https://br.investing.com/commodities/brent-oil",
    relatedTickers: ["BZ=F", "PETR4"],
    sentiment: "positive",
  },
];
